import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { AttachmentService, FileItem } from "../../../services/attachment.service";
import { AccessLevels } from "../../../pojo/Fls";
import { Item } from "../../../pojo/Item";
import { SeSecurityStore } from "../se.security.store";
import { RoleRights } from "../securityConfig/role.rights";
import {
    S25ObjectFilestorePoolComponent,
    ObjectFilestorePoolDeleteGuard,
} from "../../../standalone/s25.object.filestore.pool.component";
import { attributeRefDetailForOrgFile } from "../../../util/org.filestore.ref.util";
import { FILESTORE_POOL_MAX_FILES_PER_OBJECT, buildObjectFilestorePoolRef } from "../../../util/filestore.util";

/** Same as {@link FILESTORE_POOL_MAX_FILES_PER_OBJECT}; kept for org filestore call sites. */
export const SE_ORG_FILESTORE_MAX_FILES = FILESTORE_POOL_MAX_FILES_PER_OBJECT;

@TypeManagerDecorator("se-org-filestore")
@Component({
    selector: "se-org-filestore",
    template: `
        <s25-ng-object-filestore-pool
            [objectTypeId]="orgObjectTypeId"
            [objectId]="orgId()"
            [canView]="canView()"
            [canManage]="canManage()"
            [introText]="poolIntro()"
            uploadAriaLabel="Upload organization file"
            filesGroupLabel="organization"
            [deleteGuard]="orgDeleteGuard"
            deleteConfirmNote="This cannot be undone."
        />
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [S25ObjectFilestorePoolComponent],
})
export class SeOrgFilestoreComponent {
    readonly orgId = input.required<number>();

    private readonly securityStore = inject(SeSecurityStore);
    private readonly orgIdSig = computed(() => this.orgId());

    protected readonly orgObjectTypeId = AttachmentService.ObjectType.ORGANIZATION;

    protected readonly canView = this.securityStore.orgAccessSignal(
        this.orgIdSig,
        RoleRights.RoleRightsList.SE_ORG_FILESTORE,
        AccessLevels.Read,
    );
    protected readonly canManage = this.securityStore.orgAccessSignal(
        this.orgIdSig,
        RoleRights.RoleRightsList.SE_ORG_FILESTORE,
        AccessLevels.Full,
    );

    protected readonly poolIntro = computed(() => {
        const n = FILESTORE_POOL_MAX_FILES_PER_OBJECT;
        return `Upload up to ${n} files for this organization. They can be selected as the organization logo and referenced from custom attributes.`;
    });

    readonly orgDeleteGuard: ObjectFilestorePoolDeleteGuard = async (file: FileItem) => {
        const d = await attributeRefDetailForOrgFile(this.orgId(), {
            objectType: Item.Ids.Organization,
            objectId: this.orgId(),
            fileName: file.itemName,
        });
        return {
            blockingCount: d.refCount,
            blockingAttributeLabels: d.attributeLabels.length ? d.attributeLabels : undefined,
        };
    };

    /** Build a pool file reference (no custom attribute id) for the logo CA. */
    static buildPoolFileValue(orgId: number, fileName: string): string {
        return buildObjectFilestorePoolRef(Item.Ids.Organization, orgId, fileName);
    }
}
